import { message, Tooltip } from 'antd';
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apis from '../../apis';
import apcLogo from '../../assets/images/apc-logo.png';
import {
    ageCategories,
    guitarAgeCategoriesSolo,
    harpAgeCategoriesSolo,
} from '../../constant/RegisterPageConst';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import AssessmentForm from './AssessmentForm';
import DeadlineWarningModal from './DeadlineWarningModal';
import {
    IconArrowL,
    IconArrowR,
    IconCheck,
    IconClock, IconEdit, IconOut,
    IconSearch, IconX,
    initials
} from './Icons';
import './JuryDashboard.css';

const PAGE_SIZE = 10;

/**
 * Map age category key to full description
 */
const getAgeCategoryLabel = (ageCategoryKey) => {
    if (!ageCategoryKey) return '';
    const allAgeCategories = {
        ...ageCategories,
        ...harpAgeCategoriesSolo,
        ...guitarAgeCategoriesSolo,
    };
    return allAgeCategories[ageCategoryKey] || ageCategoryKey;
};

/**
 * Convert videoDuration (stored in seconds) to mm:ss display format
 */
const formatVideoDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '—';
    const totalSec = Math.round(Number(seconds));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Format a Firestore Timestamp or Date into a readable relative/short string
 */
const formatLastUpdated = (ts) => {
    if (!ts) return null;
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(date.getTime())) return null;
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const JuryDashboard = () => {
    const navigate = useNavigate();
    const { signOut, loggedInUser, currentEventId } = useAuth();

    // View state: 'dashboard' or 'form'
    const [view, setView] = useState('dashboard');
    const [activeRegistrant, setActiveRegistrant] = useState(null);

    // Dashboard state
    const [activeCategory, setActiveCategory] = useState('All');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [page, setPage] = useState(1);

    // Video modal state
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [loadingVideo, setLoadingVideo] = useState(false);

    // Success modal
    const [successData, setSuccessData] = useState(null);

    // Data states
    const [registrants, setRegistrants] = useState([]);
    const [juryScores, setJuryScores] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Deadline state
    const [juryDeadline, setJuryDeadline] = useState(null);
    const [showDeadlineModal, setShowDeadlineModal] = useState(false);
    const [deadlineTimeLeft, setDeadlineTimeLeft] = useState(null);

    const juryCompetitionCategory = loggedInUser?.competitionCategory;

    // Fetch jury deadline from global settings
    useEffect(() => {
        const fetchDeadline = async () => {
            try {
                const response = await apis.systemSettings.getGlobalSettings();
                const deadlines = response?.data?.juryDeadlines;
                if (deadlines && juryCompetitionCategory && deadlines[juryCompetitionCategory]) {
                    const dl = deadlines[juryCompetitionCategory];
                    setJuryDeadline(dl);

                    // Check if within 24h — show warning modal once per session
                    const deadline = new Date(dl);
                    const now = new Date();
                    const diffMs = deadline - now;
                    if (diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000) {
                        const modalKey = `deadline_warning_shown_${juryCompetitionCategory}`;
                        if (!sessionStorage.getItem(modalKey)) {
                            setShowDeadlineModal(true);
                            sessionStorage.setItem(modalKey, 'true');
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to fetch jury deadline:', err);
            }
        };
        if (juryCompetitionCategory) fetchDeadline();
    }, [juryCompetitionCategory]);

    // Live countdown for deadline
    useEffect(() => {
        if (!juryDeadline) return;
        const update = () => {
            const now = new Date();
            const end = new Date(juryDeadline);
            const diff = end - now;
            if (diff <= 0) {
                setDeadlineTimeLeft('Expired');
                return;
            }
            const d = Math.floor(diff / (1000 * 60 * 60 * 24));
            const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            if (d > 0) setDeadlineTimeLeft(`${d}d ${h}h ${m}m`);
            else if (h > 0) setDeadlineTimeLeft(`${h}h ${m}m`);
            else setDeadlineTimeLeft(`${m}m`);
        };
        update();
        const interval = setInterval(update, 30000);
        return () => clearInterval(interval);
    }, [juryDeadline]);

    // Determine urgency
    const isDeadlineUrgent = useMemo(() => {
        if (!juryDeadline) return false;
        const diff = new Date(juryDeadline) - new Date();
        return diff > 0 && diff <= 24 * 60 * 60 * 1000;
    }, [juryDeadline, deadlineTimeLeft]);

    const isDeadlinePassed = useMemo(() => {
        if (!juryDeadline) return false;
        return new Date() >= new Date(juryDeadline);
    }, [juryDeadline, deadlineTimeLeft]);

    useEffect(() => {
        if (!loggedInUser || loggedInUser.role !== 'jury') {
            message.error('Unauthorized access');
            navigate('/login');
            return;
        }
        if (juryCompetitionCategory) {
            fetchRegistrants();
        }
    }, [loggedInUser, juryCompetitionCategory]);

    const fetchRegistrants = async () => {
        try {
            setLoading(true);
            const registrantsQuery = query(
                collection(db, 'Registrants2025'),
                where('eventId', '==', currentEventId),
                where('competitionCategory', '==', juryCompetitionCategory)
            );
            const registrantsSnap = await getDocs(registrantsQuery);
            
            const juryName = (loggedInUser?.name || '').trim().toLowerCase();
            
            const registrantsData = registrantsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).filter(reg => {
                const isPaid = reg.paymentStatus === 'PAID' || 
                    (reg.invoiceStatus && reg.invoiceStatus.toLowerCase() === 'paid');
                    
                const regTeacherName = (reg.teacherName || '').trim().toLowerCase();
                const regTeacher = (reg.teacher || '').trim().toLowerCase();
                
                const isOwnStudent = juryName && (
                    (regTeacherName && regTeacherName === juryName) || 
                    (regTeacher && regTeacher === juryName)
                );
                
                return isPaid && !isOwnStudent;
            });

            const scoresQuery = query(
                collection(db, 'JuryScores2025'),
                where('juryUserId', '==', loggedInUser.uid)
            );
            const scoresSnap = await getDocs(scoresQuery);
            const scoresMap = {};
            scoresSnap.docs.forEach(doc => {
                const data = doc.data();
                scoresMap[data.registrantId] = {
                    score: data.score,
                    comment: data.comment,
                    panelComment: data.panelComment || '',
                    timestamp: data.timestamp,
                    isFinalized: data.isFinalized || false,
                };
            });

            setRegistrants(registrantsData);
            setJuryScores(scoresMap);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Failed to load registrants');
        } finally {
            setLoading(false);
        }
    };

    // Derive categories from data
    const categories = useMemo(() => {
        const cats = new Set();
        registrants.forEach(reg => {
            if (reg.PerformanceCategory === 'Ensemble') {
                cats.add('Ensemble');
            } else {
                cats.add(getAgeCategoryLabel(reg.ageCategory));
            }
        });
        return ['All', ...Array.from(cats).sort()];
    }, [registrants]);

    // Pending counts per category
    const pendingByCat = useMemo(() => {
        const out = { All: 0 };
        categories.forEach(c => { out[c] = 0; });
        registrants.forEach(p => {
            if (juryScores[p.id]?.score !== undefined) return; // already scored
            out.All += 1;
            const cat = p.PerformanceCategory === 'Ensemble' ? 'Ensemble' : getAgeCategoryLabel(p.ageCategory);
            out[cat] = (out[cat] || 0) + 1;
        });
        return out;
    }, [registrants, juryScores, categories]);

    // Totals
    const totals = useMemo(() => ({
        total: registrants.length,
        pending: registrants.filter(p => juryScores[p.id]?.score === undefined).length,
        assessed: registrants.filter(p => juryScores[p.id]?.score !== undefined).length,
    }), [registrants, juryScores]);

    // Filter + search
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return registrants.filter(p => {
            // Category filter
            if (activeCategory !== 'All') {
                const cat = p.PerformanceCategory === 'Ensemble' ? 'Ensemble' : getAgeCategoryLabel(p.ageCategory);
                if (cat !== activeCategory) return false;
            }
            // Status filter
            const isAssessed = juryScores[p.id]?.score !== undefined;
            if (statusFilter === 'Pending' && isAssessed) return false;
            if (statusFilter === 'Assessed' && !isAssessed) return false;
            // Search
            if (q) {
                const performer = p.performers?.[0];
                const name = (performer?.fullName || `${performer?.firstName || ''} ${performer?.lastName || ''}`).toLowerCase();
                if (!name.includes(q)) return false;
            }
            return true;
        }).sort((a, b) => {
            // Pending first
            const aScored = juryScores[a.id]?.score !== undefined;
            const bScored = juryScores[b.id]?.score !== undefined;
            if (aScored !== bScored) return aScored ? 1 : -1;
            return 0;
        });
    }, [registrants, activeCategory, statusFilter, search, juryScores]);

    // Pagination
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > pageCount) setPage(1); }, [pageCount, page]);
    useEffect(() => { setPage(1); }, [search, activeCategory, statusFilter]);
    const pageStart = (page - 1) * PAGE_SIZE;
    const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

    // --- Handlers ---
    const handleSave = async (registrant, scoreData) => {
        const { id: registrantId } = registrant;
        try {
            setSaving(true);
            const docId = `${registrantId}_${loggedInUser.uid}`;
            await setDoc(doc(db, 'JuryScores2025', docId), {
                registrantId,
                juryUserId: loggedInUser.uid,
                juryName: loggedInUser.name || loggedInUser.email,
                juryEmail: loggedInUser.email,
                score: scoreData.score,
                comment: scoreData.comment || '',
                panelComment: scoreData.panelComment || '',
                competitionCategory: registrant.competitionCategory || '',
                ageCategory: registrant.ageCategory || '',
                performanceCategory: registrant.PerformanceCategory || '',
                registrantName: registrant.performers?.[0]?.fullName ||
                    `${registrant.performers?.[0]?.firstName || ''} ${registrant.performers?.[0]?.lastName || ''}`.trim() || 'Unknown',
                timestamp: serverTimestamp(),
                isFinalized: false
            });

            // Update local scores
            setJuryScores(prev => ({
                ...prev,
                [registrantId]: { score: scoreData.score, comment: scoreData.comment, panelComment: scoreData.panelComment, timestamp: new Date() }
            }));

            const name = registrant.performers?.[0]?.fullName || 'contestant';
            setSuccessData({ name, score: scoreData.score });
        } catch (error) {
            console.error('Error saving assessment:', error);
            message.error('Failed to save assessment');
        } finally {
            setSaving(false);
        }
    };

    const openVideo = async (s3Link) => {
        if (!s3Link) return;
        setLoadingVideo(true);
        setVideoModalOpen(true);
        try {
            const response = await apis.aws.getPublicVideoLinkAws({ s3Link });
            setCurrentVideo(response.data.url);
        } catch (error) {
            console.error("Failed to load video", error);
            message.error("Could not load video file.");
        }
        setLoadingVideo(false);
    };

    const openPDF = async (rawLink) => {
        if (!rawLink) return;
        const s3Link = rawLink.split('&')[0].trim();
        if (s3Link.startsWith('http')) {
            window.open(s3Link, '_blank');
            return;
        }
        const hideLoading = message.loading('Opening document...', 0);
        try {
            const response = await apis.aws.getPublicVideoLinkAws({ s3Link });
            const signedUrl = response.data?.url;
            if (signedUrl) window.open(signedUrl, '_blank');
            else throw new Error("No URL returned");
        } catch (error) {
            console.error("Failed to open PDF", error);
            message.error("Could not load the document.");
        } finally {
            hideLoading();
        }
    };

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const openAssessment = (registrant) => {
        setActiveRegistrant(registrant);
        setView('form');
    };

    const backToDashboard = () => {
        setView('dashboard');
        setActiveRegistrant(null);
    };

    // Compute pending queue for "Next participant" navigation
    const pendingIds = useMemo(() =>
        registrants.filter(p => juryScores[p.id]?.score === undefined).map(p => p.id),
        [registrants, juryScores]
    );

    const pendingQueue = useMemo(() => {
        if (!activeRegistrant) return null;
        const idx = pendingIds.indexOf(activeRegistrant.id);
        if (idx === -1) return null;
        return { position: idx + 1, total: pendingIds.length };
    }, [activeRegistrant, pendingIds]);

    const goNext = () => {
        if (!activeRegistrant) return;
        const idx = pendingIds.indexOf(activeRegistrant.id);
        if (idx === -1 || idx >= pendingIds.length - 1) return;
        const nextId = pendingIds[idx + 1];
        const nextReg = registrants.find(r => r.id === nextId);
        if (nextReg) {
            setActiveRegistrant(nextReg);
        }
    };

    // --- Loading ---
    if (loading) {
        return (
            <div className="jd-spinner">
                <div className="jd-spinner-circle" />
            </div>
        );
    }

    // --- Helper: get performer name ---
    function getName(reg) {
        const p = reg.performers?.[0];
        return p?.fullName || `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
    }

    // --- Render: Video Modal ---
    function renderVideoModal() {
        return (
            <div className="jd-overlay" onClick={() => { setVideoModalOpen(false); setCurrentVideo(null); }}>
                <div className="jd-video-modal" onClick={e => e.stopPropagation()}>
                    <div className="jd-video-modal-header">
                        <div className="jd-row" style={{ gap: 10 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>Performance video</div>
                        </div>
                        <button className="jd-iconbtn" onClick={() => { setVideoModalOpen(false); setCurrentVideo(null); }}>
                            <IconX size={18} />
                        </button>
                    </div>
                    <div className="jd-video-modal-body">
                        {loadingVideo ? (
                            <div style={{ padding: '60px 0', textAlign: 'center' }}>
                                <div className="jd-spinner-circle" style={{ margin: '0 auto' }} />
                                <div style={{ color: 'var(--jd-ink-3)', marginTop: 16 }}>Loading video...</div>
                            </div>
                        ) : currentVideo ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <video
                                    id="jury-modal-video"
                                    src={currentVideo}
                                    controls
                                    preload="auto"
                                >
                                    Your browser does not support the video tag.
                                </video>
                                <div style={{ fontSize: 13, color: 'var(--jd-ink-3)', textAlign: 'center', paddingBottom: 10 }}>
                                    Experiencing lag? Pause the video to let it buffer, or{' '}
                                    <a href={currentVideo} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>
                                        open in a new tab
                                    </a>.
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="jd-video-modal-footer">
                        <div className="jd-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                            Hosted performance video
                        </div>
                        <button className="jd-btn sm primary" onClick={() => { setVideoModalOpen(false); setCurrentVideo(null); }}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- Render: Success Modal ---
    function renderSuccessModal() {
        return (
            <div className="jd-overlay">
                <div className="jd-modal">
                    <div className="jd-success-icon">
                        <IconCheck size={32} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
                        Assessment saved!
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--jd-ink-2)', marginTop: 6, lineHeight: 1.5 }}>
                        {successData.name}'s assessment has been submitted successfully.
                        {successData.score != null && <> Final score: <b>{successData.score}</b>.</>}
                    </div>
                    <button
                        className="jd-btn primary lg"
                        style={{ width: '100%', marginTop: 20 }}
                        onClick={() => { setSuccessData(null); backToDashboard(); }}
                    >
                        Back to dashboard <IconArrowR size={13} />
                    </button>
                </div>
            </div>
        );
    }

    // --- Main View ---
    return (
        <div className="jd-screen">
            {/* Nav */}
            <div className="jd-nav">
                <div className="jd-brand">
                    <img src={apcLogo} alt="APC" className="jd-brand-img" />
                </div>
                <div className="jd-nav-right">
                    {juryDeadline && deadlineTimeLeft && (
                        <div className={`jd-deadline ${isDeadlineUrgent ? 'urgent' : ''} ${isDeadlinePassed ? 'expired' : ''}`}>
                            <IconClock size={13} />
                            <span>{isDeadlinePassed ? 'Deadline passed' : `Deadline: ${deadlineTimeLeft}`}</span>
                        </div>
                    )}
                    <div className="jd-nav-user">
                        <div className="jd-nav-user-name">{loggedInUser?.name || loggedInUser?.email}</div>
                        <div className="jd-nav-user-role">Adjudicator · {juryCompetitionCategory}</div>
                    </div>
                    <div className="jd-avatar">{initials(loggedInUser?.name || loggedInUser?.email || 'U')}</div>
                    <button className="jd-btn sm ghost" onClick={handleLogout} title="Sign out">
                        <IconOut size={14} /> Sign out
                    </button>
                </div>
            </div>

            {view === 'form' && activeRegistrant ? (
                <AssessmentForm
                    registrant={activeRegistrant}
                    existingScore={juryScores[activeRegistrant.id]}
                    isFinalized={juryScores[activeRegistrant.id]?.isFinalized || false}
                    onBack={backToDashboard}
                    onSave={handleSave}
                    saving={saving}
                    onOpenVideo={openVideo}
                    onOpenPDF={openPDF}
                    loggedInUser={loggedInUser}
                    pendingQueue={pendingQueue}
                    onNext={goNext}
                />
            ) : (
                <div style={{ padding: '20px 28px', maxWidth: 1400, width: '100%', margin: '0 auto', flex: 1 }}>
                    {/* Header */}
                    <div className="jd-row jd-between" style={{ marginBottom: 18 }}>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>Assessments</div>
                            <div className="jd-label" style={{ marginTop: 4, textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                                {juryCompetitionCategory} · {totals.total} participants
                            </div>
                        </div>
                    </div>

                    {/* Summary stats */}
                    <div className="jd-stats-row">
                        <div className="jd-stat">
                            <div className="jd-label">Total Participants</div>
                            <div className="jd-stat-num">{totals.total}</div>
                            <div className="jd-stat-delta">across all categories</div>
                        </div>
                        <div className="jd-stat warn">
                            <div className="jd-label">Pending Assessments</div>
                            <div className="jd-stat-num">{totals.pending}</div>
                            <div className="jd-stat-delta">needs your attention</div>
                        </div>
                        <div className="jd-stat ok">
                            <div className="jd-label">Assessed</div>
                            <div className="jd-stat-num">{totals.assessed}</div>
                            <div className="jd-stat-delta">completed this cycle</div>
                        </div>
                    </div>

                    {/* Category tabs */}
                    <div className="jd-tabs">
                        {categories.map(c => {
                            const count = pendingByCat[c] || 0;
                            const active = activeCategory === c;
                            return (
                                <button
                                    key={c}
                                    className={`jd-tab ${active ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(c)}
                                >
                                    <span>{c}</span>
                                    {count > 0 && <span className={`jd-tab-count ${active ? 'active' : ''}`}>{count}</span>}
                                </button>
                            );
                        })}
                    </div>

                    {/* Filter bar */}
                    <div className="jd-box jd-filter-bar">
                        <div className="jd-filter-row">
                            <div className="jd-input-group">
                                <IconSearch size={14} />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Search by participant name…"
                                />
                                {search && (
                                    <button className="jd-clear-btn" onClick={() => setSearch('')} title="Clear">
                                        <IconX size={14} />
                                    </button>
                                )}
                            </div>
                            <select className="jd-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="All">All statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Assessed">Assessed</option>
                            </select>
                            <div className="jd-grow" />
                            <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                                {filtered.length === 0 ? 'no results'
                                    : filtered.length === registrants.length ? `${filtered.length} results`
                                        : `${filtered.length} of ${registrants.length}`}
                            </span>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="jd-box" style={{ padding: 0, overflow: 'hidden' }}>
                        <table className="jd-table">
                            <thead>
                                <tr>
                                    <th>Participant / Group</th>
                                    <th>Category</th>
                                    <th>Duration</th>
                                    <th>Score</th>
                                    <th>Status</th>
                                    <th>Last Updated</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="jd-table-empty">
                                        No participants match your filters.
                                    </td></tr>
                                )}
                                {pageRows.map(p => {
                                    const isAssessed = juryScores[p.id]?.score !== undefined;
                                    const pName = getName(p);
                                    const isEnsemble = p.PerformanceCategory === 'Ensemble';
                                    return (
                                        <tr key={p.id} className={isAssessed ? 'muted' : ''}>
                                            <td>
                                                <div className="jd-row" style={{ gap: 10 }}>
                                                    <span className="jd-avatar sm">{initials(pName)}</span>
                                                    <div>
                                                        <div className="jd-name" style={{ fontWeight: 600, fontSize: 13 }}>{pName}</div>
                                                        <div className="jd-label" style={{ fontSize: 10, marginTop: 2 }}>
                                                            {isEnsemble ? (
                                                                <Tooltip 
                                                                    title={p.performers?.map(perf => perf.fullName || `${perf.firstName || ''} ${perf.lastName || ''}`.trim()).join(', ')}
                                                                    placement="topLeft"
                                                                >
                                                                    <span style={{ cursor: 'help', borderBottom: '1px dotted var(--jd-ink-3)' }}>
                                                                        Group · {p.performers?.length || 0} members
                                                                    </span>
                                                                </Tooltip>
                                                            ) : 'Individual'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{isEnsemble ? 'Ensemble' : getAgeCategoryLabel(p.ageCategory)}</td>
                                            <td>
                                                {p.videoDuration ? (
                                                    <span className="jd-row" style={{ gap: 4, color: 'var(--jd-ink-2)', fontSize: 12 }}>
                                                        <IconClock size={12} /> {formatVideoDuration(p.videoDuration)}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: 'var(--jd-ink-4)' }}>—</span>
                                                )}
                                            </td>
                                            <td>
                                                {isAssessed ? (
                                                    <span style={{
                                                        fontWeight: 600,
                                                        fontSize: 16,
                                                        fontVariantNumeric: 'tabular-nums',
                                                        letterSpacing: '-0.02em',
                                                    }}>
                                                        <span className="jd-hi">{juryScores[p.id].score}</span>
                                                    </span>
                                                ) : (
                                                    <span style={{ color: 'var(--jd-ink-4)' }}>—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`jd-pill dot ${isAssessed ? 'ok' : 'warn'}`}>
                                                    {isAssessed ? 'Assessed' : 'Pending'}
                                                </span>
                                            </td>
                                            <td>
                                                {isAssessed && juryScores[p.id]?.timestamp ? (
                                                    <span style={{ fontSize: 12, color: 'var(--jd-ink-3)' }}>
                                                        {formatLastUpdated(juryScores[p.id].timestamp)}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: 'var(--jd-ink-4)' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {!isAssessed ? (
                                                    <button className="jd-btn sm primary" onClick={() => openAssessment(p)}>
                                                        Assess <IconArrowR size={12} />
                                                    </button>
                                                ) : juryScores[p.id]?.isFinalized ? (
                                                    <span className="jd-pill" style={{ fontSize: 11, color: 'var(--jd-ink-3)' }}>🔒 Finalized</span>
                                                ) : (
                                                    <div className="jd-row jd-flex-end" style={{ gap: 6 }}>
                                                        <button className="jd-btn sm" onClick={() => openAssessment(p)}>
                                                            <IconEdit size={12} /> Edit
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {filtered.length > 0 && (
                            <div className="jd-pagination">
                                <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                                    Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
                                </span>
                                <div className="jd-row" style={{ gap: 4 }}>
                                    <button className="jd-btn sm ghost" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                                    <button className="jd-btn sm ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                                        <IconArrowL size={12} /> Prev
                                    </button>
                                    {Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
                                        <button
                                            key={n}
                                            className={`jd-btn sm ${page === n ? 'primary' : 'ghost'}`}
                                            style={{ minWidth: 32 }}
                                            onClick={() => setPage(n)}
                                        >{n}</button>
                                    ))}
                                    <button className="jd-btn sm ghost" onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page === pageCount}>
                                        Next <IconArrowR size={12} />
                                    </button>
                                    <button className="jd-btn sm ghost" onClick={() => setPage(pageCount)} disabled={page === pageCount}>»</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {videoModalOpen && renderVideoModal()}
            {successData && renderSuccessModal()}
            {showDeadlineModal && juryDeadline && (
                <DeadlineWarningModal
                    deadline={juryDeadline}
                    category={juryCompetitionCategory}
                    onDismiss={() => setShowDeadlineModal(false)}
                />
            )}
        </div>
    );
};

export default JuryDashboard;